import { useState } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import { Sparkle, Copy, Download, MagicWand } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import AppLayout from '@/components/AppLayout';
import BackButton from '@/components/BackButton';
import SEOAnalysis from '@/components/SEOAnalysis';
import PageTransition from '@/components/PageTransition';
import { useTheme } from '@/contexts/ThemeContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function Generate() {
  const { isDark } = useTheme();
  const [formData, setFormData] = useState({
    activity: '',
    city: '',
    objective: '',
    tone: 'professionnel',
  });
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleGenerate = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/content/generate`,
        formData,
        { withCredentials: true }
      );
      setResults(response.data);
      toast.success('Contenu généré avec succès !');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la génération');
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copié dans le presse-papier');
  };

  const saveContent = async (platform, content) => {
    try {
      await axios.post(
        `${BACKEND_URL}/api/content/save`,
        { type: 'post', platform, content },
        { withCredentials: true }
      );
      toast.success('Contenu sauvegardé');
    } catch (error) {
      toast.error('Erreur lors de la sauvegarde');
    }
  };

  return (
    <AppLayout>
      <PageTransition>
        <div className="p-8 max-w-7xl mx-auto relative z-10" data-testid="generate-page">
          {/* Back Button */}
          <BackButton to="/dashboard" label="Retour au tableau de bord" className="mb-4" />
          
          <motion.div
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            className="mb-8"
          >
            <h1 className={`text-4xl md:text-5xl font-bold mb-3 ${isDark ? 'text-white' : 'text-gray-900'}`}>
              Générateur de contenu
            </h1>
            <p className={`text-lg ${isDark ? 'text-white/50' : 'text-gray-600'}`}>
              Créez du contenu optimisé pour tous vos canaux en quelques secondes
            </p>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Form */}
            <motion.div
              initial={{ x: -20, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              transition={{ delay: 0.1 }}
            >
              <div className={`p-6 rounded-2xl ${
                isDark 
                  ? 'glass-card' 
                  : 'bg-white border border-gray-200 shadow-sm'
              }`}>
                <div className="flex items-center gap-3 mb-6">
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                    isDark 
                      ? 'bg-gradient-to-br from-violet-500/20 to-pink-500/20' 
                      : 'bg-violet-100'
                  }`}>
                    <MagicWand size={20} weight="duotone" className="text-violet-500" />
                  </div>
                  <h2 className={`text-xl font-semibold ${isDark ? 'text-white/90' : 'text-gray-900'}`}>
                    Informations
                  </h2>
                </div>
                
                <form onSubmit={handleGenerate} className="space-y-5">
                  <div className="space-y-2">
                    <Label htmlFor="activity" className={isDark ? 'text-white/70' : 'text-gray-700'}>
                      Votre activité
                    </Label>
                    <Input
                      id="activity"
                      placeholder="Ex: Restaurant italien, Salon de coiffure..."
                      value={formData.activity}
                      onChange={(e) => setFormData({ ...formData, activity: e.target.value })}
                      required
                      data-testid="generate-activity-input"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="city" className={isDark ? 'text-white/70' : 'text-gray-700'}>
                      Ville
                    </Label>
                    <Input
                      id="city"
                      placeholder="Ex: Paris, Lyon..."
                      value={formData.city}
                      onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                      required
                      data-testid="generate-city-input"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="objective" className={isDark ? 'text-white/70' : 'text-gray-700'}>
                      Objectif
                    </Label>
                    <Input
                      id="objective"
                      placeholder="Ex: Attirer plus de clients, Promouvoir une offre..."
                      value={formData.objective}
                      onChange={(e) => setFormData({ ...formData, objective: e.target.value })}
                      required
                      data-testid="generate-objective-input"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="tone" className={isDark ? 'text-white/70' : 'text-gray-700'}>
                      Ton
                    </Label>
                    <Select
                      value={formData.tone}
                      onValueChange={(value) => setFormData({ ...formData, tone: value })}
                    >
                      <SelectTrigger data-testid="generate-tone-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="professionnel">Professionnel</SelectItem>
                        <SelectItem value="amical">Amical</SelectItem>
                        <SelectItem value="enthousiaste">Enthousiaste</SelectItem>
                        <SelectItem value="informel">Informel</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <Button
                    type="submit"
                    className="w-full btn-premium py-6"
                    disabled={loading}
                    data-testid="generate-submit-btn"
                  >
                    {loading ? (
                      <span className="flex items-center gap-2">
                        <div className="w-4 h-4 border-2 border-white/20 border-t-white rounded-full animate-spin"></div>
                        Génération en cours...
                      </span>
                    ) : (
                      <>
                        <Sparkle className="mr-2" size={20} weight="duotone" />
                        Générer pour moi
                      </>
                    )}
                  </Button>
                </form>
              </div>
            </motion.div>

            {/* Results */}
            <motion.div
              initial={{ x: 20, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              transition={{ delay: 0.2 }}
              className="space-y-6"
            >
              {!results && (
                <div className={`p-12 flex flex-col items-center justify-center text-center min-h-[400px] rounded-2xl ${
                  isDark 
                    ? 'glass-card' 
                    : 'bg-white border border-gray-200 shadow-sm'
                }`}>
                  <div className={`w-20 h-20 rounded-2xl flex items-center justify-center mb-6 ${
                    isDark 
                      ? 'bg-gradient-to-br from-violet-500/15 to-pink-500/15' 
                      : 'bg-violet-100'
                  }`}>
                    <Sparkle size={40} weight="duotone" className="text-violet-500" />
                  </div>
                  <h3 className={`text-xl font-semibold mb-3 ${isDark ? 'text-white/90' : 'text-gray-900'}`}>
                    Prêt à créer
                  </h3>
                  <p className={`max-w-sm ${isDark ? 'text-white/50' : 'text-gray-600'}`}>
                    Remplissez le formulaire et cliquez sur "Générer pour moi" pour obtenir votre contenu
                  </p>
                </div>
              )}

              {results && (
                <>
                  {results.seo_analysis && results.seo_suggestions && (
                    <SEOAnalysis 
                      analysis={results.seo_analysis} 
                      suggestions={results.seo_suggestions} 
                    />
                  )}
                  
                  <ContentCard
                    title="Post Instagram"
                    content={results.instagram}
                    onCopy={() => copyToClipboard(results.instagram)}
                    onSave={() => saveContent('instagram', results.instagram)}
                    isDark={isDark}
                  />
                  <ContentCard
                    title="Post Facebook"
                    content={results.facebook}
                    onCopy={() => copyToClipboard(results.facebook)}
                    onSave={() => saveContent('facebook', results.facebook)}
                    isDark={isDark}
                  />
                  <ContentCard
                    title="Texte SEO Google"
                    content={results.seo}
                    onCopy={() => copyToClipboard(results.seo)}
                    onSave={() => saveContent('google', results.seo)}
                    isDark={isDark}
                  />
                  <ContentCard
                    title="Hashtags"
                    content={results.hashtags}
                    onCopy={() => copyToClipboard(results.hashtags)}
                    onSave={() => saveContent('hashtags', results.hashtags)}
                    isDark={isDark}
                  />
                  <ContentCard
                    title="Description marketing"
                    content={results.description}
                    onCopy={() => copyToClipboard(results.description)}
                    onSave={() => saveContent('description', results.description)}
                    isDark={isDark}
                  />
                </>
              )}
            </motion.div>
          </div>
        </div>
      </PageTransition>
    </AppLayout>
  );
}

function ContentCard({ title, content, onCopy, onSave, isDark }) {
  return (
    <div className={`p-6 rounded-2xl ${
      isDark 
        ? 'glass-card' 
        : 'bg-white border border-gray-200 shadow-sm'
    }`}>
      <div className="flex items-center justify-between mb-4">
        <h3 className={`font-semibold ${isDark ? 'text-white/90' : 'text-gray-900'}`}>{title}</h3>
        <div className="flex items-center gap-2">
          <Button 
            size="sm" 
            variant="ghost" 
            onClick={onCopy} 
            className={isDark ? 'hover:bg-white/[0.06] text-white/50 hover:text-white/80' : 'hover:bg-gray-100'}
            data-testid={`copy-${title.toLowerCase().replace(/\s/g, '-')}`}
          >
            <Copy size={16} />
          </Button>
          <Button 
            size="sm" 
            variant="ghost" 
            onClick={onSave}
            className={isDark ? 'hover:bg-white/[0.06] text-white/50 hover:text-white/80' : 'hover:bg-gray-100'}
            data-testid={`save-${title.toLowerCase().replace(/\s/g, '-')}`}
          >
            <Download size={16} />
          </Button>
        </div>
      </div>
      <Textarea
        value={content}
        readOnly
        className={`min-h-[120px] resize-none ${
          isDark 
            ? 'bg-white/[0.02] border-white/[0.06] text-white/80' 
            : 'bg-gray-50 border-gray-200 text-gray-700'
        }`}
      />
    </div>
  );
}
